import { cn } from '@/common/lib/cn/cn'
import { Button } from '@/common/ui/Form/Button'
import { Input, type InputType } from '@/common/ui/Form/Input'
import { Icon } from '@/common/ui/Icon'
import React, { useEffect, useRef, useState } from 'react'

type InlineEditProps = {
    value: string
    onSave: (nextValue: string) => void
    placeholder?: string
    disabled?: boolean
    className?: string
    inputSize?: InputType['size']
    inputEmphasis?: InputType['emphasis']
    saveOnBlur?: boolean
}

export const InlineEdit = ({
    value,
    onSave,
    placeholder = 'Нажмите, чтобы изменить',
    disabled = false,
    className,
    inputSize = 'md',
    inputEmphasis = 'neutral',
    saveOnBlur = true,
}: InlineEditProps) => {
    const [isEditing, setIsEditing] = useState(false)
    const [draftValue, setDraftValue] = useState(value)
    const inputRef = useRef<HTMLInputElement>(null)
    const isCommittingRef = useRef(false)

    useEffect(() => {
        setDraftValue(value)
    }, [value])

    useEffect(() => {
        if (!isEditing) {
            return
        }

        inputRef.current?.focus()
        inputRef.current?.select()
    }, [isEditing])

    const handleStartEdit = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.stopPropagation()
        if (disabled) {
            return
        }

        setDraftValue(value)
        setIsEditing(true)
    }

    const handleCancel = () => {
        setDraftValue(value)
        setIsEditing(false)
    }

    const handleSave = () => {
        const nextValue = draftValue.trim()
        isCommittingRef.current = true
        onSave(nextValue)
        setIsEditing(false)
        setTimeout(() => {
            isCommittingRef.current = false
        }, 0)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            handleSave()
            return
        }

        if (event.key === 'Escape') {
            event.preventDefault()
            handleCancel()
        }
    }

    const handleBlur = () => {
        if (!saveOnBlur || isCommittingRef.current) {
            return
        }

        handleSave()
    }

    if (isEditing) {
        return (
            <div
                className={cn('flex items-center gap-2', className)}
                onClick={event => event.stopPropagation()}
                onKeyDown={event => event.stopPropagation()}
            >
                <Input
                    ref={inputRef}
                    value={draftValue}
                    onChange={event => setDraftValue(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    size={inputSize}
                    emphasis={inputEmphasis}
                    disabled={disabled}
                    active
                />
                <Button
                    type='button'
                    size='sm'
                    onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                    onClick={handleSave}
                >
                    Сохранить
                </Button>
                <Button
                    type='button'
                    size='sm'
                    emphasis='secondary'
                    variant='outline'
                    onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                    onClick={handleCancel}
                >
                    Отмена
                </Button>
            </div>
        )
    }

    return (
        <button
            type='button'
            className={cn(
                'group inline-flex max-w-full items-center gap-2 rounded-md border border-transparent px-2 py-1 text-left transition-colors hover:border-[var(--pix-border-accent)] hover:bg-[var(--pix-bg-primary-light)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pix-border-accent)]',
                disabled && 'pointer-events-none opacity-50',
                className
            )}
            onClick={handleStartEdit}
            onMouseDown={event => event.stopPropagation()}
            disabled={disabled}
            aria-label='Редактировать значение'
        >
            <span className={cn('truncate text-sm', !value && 'text-[var(--pix-text-tertiary)]')}>
                {value || placeholder}
            </span>
            <span className='text-xs text-[var(--pix-text-secondary)] opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100'>
                <Icon name='edit-2' size='sm' />
            </span>
        </button>
    )
}
